import { CompanyContactId, Dtos, SuretyAccountId } from "@inrev/common";
import { useGlobalErrorMessageModal } from "@portal/providers/GlobalErrorHandlingProvider";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { logError } from "../../utils/error";
import { useRequest } from "../../utils/request";
import { accountCompaniesQueryKey, accountQueryKey } from "./account.keys";

export const useUpdateAccountCompany = (accountId: SuretyAccountId) => {
	const { put } = useRequest();
	const queryClient = useQueryClient();
	const { triggerErrorModal } = useGlobalErrorMessageModal();

	const companiesQueryKey = accountCompaniesQueryKey(accountId);

	const mutation = useMutation({
		mutationFn: ({
			companyId,
			data,
		}: {
			companyId: CompanyContactId;
			data: Dtos.Company.Update.Request;
			onSuccess?: () => void;
		}) =>
			put<Dtos.Company.Get.Response>(
				`/v2/surety/accounts/${accountId}/companies/${companyId}`,
				data,
			),
		onMutate: async ({ companyId, data }) => {
			await queryClient.cancelQueries({ queryKey: companiesQueryKey });
			const previousCompanies =
				queryClient.getQueryData<Dtos.Company.Get.Response[]>(companiesQueryKey);

			queryClient.setQueryData<Dtos.Company.Get.Response[]>(companiesQueryKey, (old) => {
				if (!old) return [];
				return old.map((c) =>
					c.id === companyId
						? { ...c, name: data.name, address: data.address, fein: data.fein ?? undefined }
						: c,
				);
			});

			return { previousCompanies };
		},
		onSuccess: (updatedCompany, variables) => {
			queryClient.setQueryData<Dtos.Company.Get.Response[]>(companiesQueryKey, (old) => {
				if (!old) return [updatedCompany];
				return old.map((c) => (c.id === variables.companyId ? updatedCompany : c));
			});
			queryClient.invalidateQueries({ queryKey: accountQueryKey(accountId) });
			variables.onSuccess?.();
		},
		onError: (error, _variables, context) => {
			logError(error);
			triggerErrorModal(error);
			if (context?.previousCompanies) {
				queryClient.setQueryData(companiesQueryKey, context.previousCompanies);
			}
			queryClient.invalidateQueries({ queryKey: companiesQueryKey });
		},
	});

	return {
		updateAccountCompany: mutation.mutate,
		updateAccountCompanyIsLoading: mutation.isPending,
		updateAccountCompanyError: mutation.error,
	};
};
